import { useEffect, useState } from "react";
import { fetchHealthDeep } from "@/lib/api";
import {
  Activity, Database, Phone, Mail, Image, Radio, BarChart3,
  CheckCircle2, AlertCircle, XCircle, RefreshCw, Loader2,
} from "lucide-react";

const DEP_META = {
  mongo:           { label: "MongoDB",        icon: Database, desc: "Base de données" },
  africastalking:  { label: "SMS OTP",        icon: Phone,    desc: "Africa's Talking" },
  resend:          { label: "Emails",         icon: Mail,     desc: "Resend" },
  cloudinary:      { label: "Images CDN",     icon: Image,    desc: "Cloudinary" },
  sse_broker:      { label: "Live stream",    icon: Radio,    desc: "SSE temps réel" },
  data:            { label: "Données",        icon: BarChart3, desc: "Volume annonces" },
};

const STATUS_STYLE = {
  ok:       { bg: "bg-emerald-50",  ring: "ring-emerald-300", text: "text-emerald-700", Icon: CheckCircle2, label: "OK" },
  degraded: { bg: "bg-amber-50",    ring: "ring-amber-300",   text: "text-amber-700",   Icon: AlertCircle, label: "DEGRADE" },
  down:     { bg: "bg-red-50",      ring: "ring-red-400",     text: "text-red-700",     Icon: XCircle,      label: "DOWN" },
};

function pillStatus(check) {
  if (!check?.ok) return "down";
  // For optional services like Resend, `enabled: false` means console-only mode
  if (check.enabled === false) return "degraded";
  return "ok";
}

export default function AdminHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const d = await fetchHealthDeep();
      setData(d);
      setError(null);
      setLastFetch(new Date());
    } catch (e) {
      setError(e?.message || "fetch failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => { setRefreshing(true); load(); }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const globalStatus = data?.status || (error ? "down" : "ok");
  const globalStyle = STATUS_STYLE[globalStatus] || STATUS_STYLE.ok;

  return (
    <section className="glass-card p-5 lg:col-span-3" data-testid="admin-health-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="font-bold text-lg inline-flex items-center gap-2">
            <Activity size={18} className="text-orange-600" /> Santé de la plateforme
          </h2>
          <p className="text-xs text-[var(--ink-soft)]">
            Auto-rafraîchi toutes les 30 s · Endpoint <code>/api/health/deep</code>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data?.elapsed_ms != null && (
            <span className="text-xs text-[var(--ink-soft)]" data-testid="health-elapsed">
              {data.elapsed_ms} ms
            </span>
          )}
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ring-2 ${globalStyle.bg} ${globalStyle.ring} ${globalStyle.text}`}
            data-testid="health-global-status"
          >
            <globalStyle.Icon size={14} /> {globalStyle.label}
          </span>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            className="p-1.5 rounded-full hover:bg-orange-50 text-[var(--ink-soft)] hover:text-orange-600 transition"
            title="Rafraîchir maintenant"
            data-testid="health-refresh-btn"
          >
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-[var(--ink-soft)] py-4 text-center">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Chargement…
        </div>
      )}

      {!loading && error && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-2xl">
          Impossible de joindre l'endpoint santé : {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="health-pills-grid">
            {Object.entries(DEP_META).map(([key, meta]) => {
              const check = data.checks?.[key];
              const status = pillStatus(check);
              const s = STATUS_STYLE[status];
              return (
                <div
                  key={key}
                  className={`rounded-2xl p-3 ring-2 ${s.bg} ${s.ring} transition`}
                  data-testid={`health-pill-${key}`}
                  title={JSON.stringify(check, null, 2)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <meta.icon size={16} className={s.text} />
                    <s.Icon size={14} className={s.text} />
                  </div>
                  <div className={`text-sm font-bold ${s.text}`}>{meta.label}</div>
                  <div className="text-[10px] text-[var(--ink-soft)] truncate">
                    {check?.mode || check?.subscribers != null && `${check.subscribers} live`
                      || check?.annonces_total != null && `${check.annonces_total} ann.`
                      || meta.desc}
                  </div>
                </div>
              );
            })}
          </div>
          {lastFetch && (
            <div className="text-[11px] text-[var(--ink-soft)] mt-3 text-right">
              Dernière vérification : {lastFetch.toLocaleTimeString("fr-FR")}
            </div>
          )}
        </>
      )}
    </section>
  );
}
